import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../config/firebase/app';
import brand from '../config/brand';

type DebugModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type FirebaseUserInfo = {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  providerData: any[];
  metadata: {
    creationTime: string;
    lastSignInTime: string;
  };
  claims: any;
};

const DebugModal: React.FC<DebugModalProps> = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState<FirebaseUserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const fetchUserInfo = async () => {
        try {
          const currentUser = auth.currentUser;
          if (currentUser) {
            const idTokenResult = await currentUser.getIdTokenResult();
            setUserInfo({
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              emailVerified: currentUser.emailVerified,
              providerData: currentUser.providerData,
              metadata: {
                creationTime: currentUser.metadata.creationTime || '',
                lastSignInTime: currentUser.metadata.lastSignInTime || '',
              },
              claims: idTokenResult.claims,
            });
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchUserInfo();
    }
  }, [open]);

  const formatClaims = (claims: any) => {
    return Object.entries(claims).map(([key, value]) => (
      <div key={key} className="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
        <span className="font-mono text-sm text-gray-600">{key}:</span>
        <span className="font-mono text-sm text-gray-900 break-all">
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
        </span>
      </div>
    ));
  };

  return (
    <Transition show={open}>
      <Dialog className="relative z-[100]" onClose={setOpen}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center text-center sm:items-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-4xl sm:p-6">
                <div className="absolute top-2 right-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                    Debug Information
                  </DialogTitle>
                  <p className="text-sm text-gray-500 mt-2">
                    Firebase Auth Info & Brand Configuration
                  </p>
                </div>

                <div className="mt-6 space-y-6">
                  {/* Brand Information */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Brand Configuration</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Active Brand:</span>
                        <span className="font-mono text-sm">{brand.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Brand Code:</span>
                        <span className="font-mono text-sm">{brand.code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Hostname:</span>
                        <span className="font-mono text-sm">{window.location.hostname}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Domain:</span>
                        <span className="font-mono text-sm">{brand.domain}</span>
                      </div>
                    </div>
                  </div>

                  {/* Environment Variables */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Environment Variables</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium">NODE_ENV:</span>
                        <span className="font-mono text-sm">{process.env.NODE_ENV || 'undefined'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">REACT_APP_LOCAL_DEV:</span>
                        <span className="font-mono text-sm">{process.env.REACT_APP_LOCAL_DEV || 'undefined'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">REACT_APP_MRR_ENVIRONMENT:</span>
                        <span className="font-mono text-sm">{process.env.REACT_APP_MRR_ENVIRONMENT || 'undefined'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Firebase User Information */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Firebase User Info</h4>
                    {loading ? (
                      <div className="text-center py-4">
                        <span className="text-gray-500">Loading user information...</span>
                      </div>
                    ) : userInfo ? (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="font-medium">UID:</span>
                              <span className="font-mono text-sm text-gray-600">{userInfo.uid}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Email:</span>
                              <span className="font-mono text-sm text-gray-600">{userInfo.email || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Display Name:</span>
                              <span className="font-mono text-sm text-gray-600">{userInfo.displayName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Email Verified:</span>
                              <span className="font-mono text-sm text-gray-600">{userInfo.emailVerified ? 'Yes' : 'No'}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="font-medium">Created:</span>
                              <span className="font-mono text-sm text-gray-600">
                                {userInfo.metadata.creationTime ? new Date(userInfo.metadata.creationTime).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Last Sign In:</span>
                              <span className="font-mono text-sm text-gray-600">
                                {userInfo.metadata.lastSignInTime ? new Date(userInfo.metadata.lastSignInTime).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No user information available
                      </div>
                    )}
                  </div>

                  {/* JWT Claims */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">JWT Claims</h4>
                    {loading ? (
                      <div className="text-center py-4">
                        <span className="text-gray-500">Loading claims...</span>
                      </div>
                    ) : userInfo?.claims ? (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-1">
                          {formatClaims(userInfo.claims)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No claims available
                      </div>
                    )}
                  </div>

                  {/* Provider Data */}
                  {userInfo?.providerData && userInfo.providerData.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Provider Data</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-2">
                          {userInfo.providerData.map((provider, index) => (
                            <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0">
                              <div className="font-medium text-sm">{provider.providerId}</div>
                              <div className="text-sm text-gray-600">
                                UID: {provider.uid}
                              </div>
                              {provider.email && (
                                <div className="text-sm text-gray-600">
                                  Email: {provider.email}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DebugModal;
